import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { memo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import ListGender from 'shell/components/shared/gender';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { DatePicker } from '@mui/lab';
import { AccordionGeneralityFormVM } from 'front-office/view-models/certificate/generality-form';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import MunicipalityList from 'shell/components/shared/municipality-list';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import ChooseDate from 'shell/components/shared/choice-date';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';
import ItalianForeignState from '../generality/italian-foreign-state';
import { noop } from 'shell/utils/noop';

interface AccordionApplicantFormProps {
  disabled?: boolean;
  values?: AccordionApplicantFormVM;
  errors?: FormikErrors<AccordionApplicantFormVM>;
  touched?: FormikTouched<AccordionApplicantFormVM>;
  generality?: AccordionGeneralityFormVM;
  isForAttestazione?: boolean;
  isAuthentic?: boolean;
  onChange: (
    key: keyof AccordionApplicantFormVM,
    value: AccordionApplicantFormVM[keyof AccordionApplicantFormVM]
  ) => void;
}
const AccordionApplicantFormComponent = ({
  disabled,
  values,
  onChange,
  generality,
  isForAttestazione,
  errors,
  touched,
  isAuthentic,
}: AccordionApplicantFormProps): React.ReactElement => {
  const formContext = useFormikContext<CertificateFormVM>();

  // Handle surname
  const handleSurname = useEventCallback((value: string) => {
    onChange('surname', value);
  });

  // Handle name
  const handleName = useEventCallback((value: string) => {
    onChange('name', value);
  });

  // Handle gender
  const handleGender = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('gender', event.target.value);
  });

  //Handle on change  to change an particular field of accountholder document
  const onChangeFieldAccountholder = useEventCallback(
    (
      key: keyof AccordionGeneralityFormVM,
      value: AccordionGeneralityFormVM[keyof AccordionGeneralityFormVM]
    ) => {
      formContext.setFieldValue('accountholder', {
        ...(formContext.values.accountholder || {}),
        account: {
          ...(formContext.values.accountholder?.account || {}),
          generality: {
            ...(formContext?.values?.accountholder?.account?.generality || {}),
            [key]: value,
          },
        },
      });
    }
  );

  //Handle on change  to change an particular field of accountholder document
  const onChangeFieldsAccountholder = useEventCallback(
    (generalityValues: AccordionGeneralityFormVM) => {
      formContext.setFieldValue('accountholder', {
        ...(formContext.values.accountholder || {}),
        account: {
          ...(formContext.values.accountholder?.account || {}),
          generality: {
            ...(formContext?.values?.accountholder?.account?.generality || {}),
            ...generalityValues,
          },
        },
      });
    }
  );

  const errorAccountholderGenerality = (
    (formContext?.errors?.accountholder as FormikErrors<AccordionAccountholderFormVM>)
      ?.account as FormikErrors<{
      applicant?: AccordionApplicantFormVM;
      generality?: AccordionGeneralityFormVM;
    }>
  )?.generality as FormikErrors<AccordionGeneralityFormVM>;

  const touchedAccountholderGenerality = (
    (formContext?.touched?.accountholder as unknown as FormikTouched<AccordionAccountholderFormVM>)
      ?.account as unknown as FormikTouched<{
      applicant?: AccordionApplicantFormVM;
      generality?: AccordionGeneralityFormVM;
    }>
  )?.generality as unknown as FormikTouched<AccordionGeneralityFormVM>;

  return (
    <Grid container item xs={12} spacing={0} columnSpacing={{ xs: 0, md: 1 }} rowSpacing={1}>
      {formContext?.values?.certificateInternOnly ? (
        <Grid item xs={12} md={8}>
          <PmiTextField
            label="Motivo rilascio"
            aria-label="motivo rilascio"
            id="releaseReason"
            fullWidth
            value={values?.releaseReason || ''}
            error={touched?.releaseReason && Boolean(errors?.releaseReason)}
            helperText={errors?.releaseReason}
            multiline
            type="text"
            rows={2}
            onChange={(event) => {
              onChange('releaseReason', event?.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Codice fiscale *"
          aria-label="Codice fiscale"
          id="fiscal-code"
          fullWidth
          onChange={(event) => {
            onChange('fiscalCode', event?.target.value);
          }}
          value={values?.fiscalCode?.toUpperCase() || ''}
          inputProps={{ maxLength: '16' }}
          disabled={disabled}
          error={touched?.fiscalCode && Boolean(errors?.fiscalCode)}
          helperText={errors?.fiscalCode}
        />
      </Grid>
      <Grid
        item
        container
        spacing={0}
        columnSpacing={{ xs: 0, md: 1 }}
        xs={12}
        md={10}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <InputKeyboard
            label="Cognome *"
            id="surname"
            aria-label="Cognome"
            onChange={handleSurname}
            disabled={disabled}
            value={values?.surname || ''}
            touched={touched?.surname}
            error={errors?.surname}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname"
                checked={values?.withoutSurname || false}
                onChange={() => onChange('withoutSurname', !values?.withoutSurname)}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid
        item
        container
        spacing={0}
        columnSpacing={{ xs: 0, md: 1 }}
        xs={12}
        md={10}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <InputKeyboard
            label="Nome *"
            aria-label="Nome"
            id="name"
            disabled={disabled}
            onChange={handleName}
            value={values?.name || ''}
            touched={touched?.name}
            error={errors?.name}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName"
                disabled={disabled}
                checked={values?.withoutName || false}
                onChange={() => onChange('withoutName', !values?.withoutName)}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={5}>
        <ListGender
          required
          value={values?.gender || ''}
          disabled={disabled}
          onChange={handleGender}
          error={touched?.gender && Boolean(errors?.gender)}
        />
      </Grid>
      <Grid item xs={12} container spacing={0} columnSpacing={{ xs: 0, md: 1 }} alignItems="center">
        <ChooseDate
          selected={values?.birthDate || null}
          onChange={(date, choice) =>
            formContext.setValues({
              ...(formContext.values || {}),
              accountholder: {
                ...(formContext.values?.accountholder || {}),
                account: {
                  ...(formContext.values?.accountholder?.account || {}),
                  applicant: {
                    ...(formContext.values?.accountholder?.account?.applicant || {}),
                    birthDate: date,
                    birthChoiceDate: choice,
                  },
                },
              },
            })
          }
          choice={values?.birthChoiceDate}
          disabled={disabled}
        />
      </Grid>
      {isAuthentic ? (
        <Grid item xs={12}>
          <ItalianForeignState
            isForeign={generality?.isForeign}
            foreignStateBirth={generality?.foreignStateBirth}
            foreignMunicipality={generality?.foreignMunicipality}
            provinceOfBirth={generality?.provinceOfBirth}
            municipalityOfBirth={generality?.municipalityOfBirth}
            locality={generality?.locality}
            values={generality}
            onChange={onChangeFieldAccountholder}
            onChangeValues={onChangeFieldsAccountholder || noop}
            errors={errors}
            touched={touched}
            isAuthentic={isAuthentic}
          />
        </Grid>
      ) : undefined}
      {isForAttestazione ? (
        <Grid item container spacing={0} columnSpacing={{ xs: 0, md: 1 }} alignItems="center">
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <PmiTitleDivider>Documento di riconoscimento</PmiTitleDivider>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Documento di riconoscimento"
                id="isForeignDocumentAccountholder"
                value={generality?.isForeignDocument || DocumentRadioCertificate.IdentityCard}
                onChange={(event) => {
                  formContext.setValues({
                    ...(formContext.values || {}),
                    accountholder: {
                      ...(formContext.values?.accountholder || {}),
                      account: {
                        ...(formContext.values?.accountholder?.account || {}),
                        generality: {
                          ...(formContext.values?.accountholder?.account?.generality || {}),
                          isForeignDocument: event.target.value as DocumentRadioCertificate,
                          consulateCode: '',
                          witness1: '',
                          witness1IdentityCard: '',
                          witness2: '',
                          witness2IdentityCard: '',
                          numberDocument: '',
                          dateReleaseDocument: null,
                          provinceDocument: '',
                          municipalityDocument: null,
                          localityDocument: '',
                        },
                      },
                    },
                  });
                }}
              >
                <FormControlLabel
                  // onClick={() => {
                  //   resetItalian();
                  // }}
                  value={DocumentRadioCertificate.IdentityCard}
                  control={<Radio />}
                  label="Carta d'identità"
                />
                <FormControlLabel
                  // onClick={() => {
                  //   resetForeign();
                  // }}
                  value={DocumentRadioCertificate.Other}
                  control={<Radio />}
                  label="Altro"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Numero *"
              aria-label="Numero"
              fullWidth
              helperText={errorAccountholderGenerality?.numberDocument}
              error={
                Boolean(errorAccountholderGenerality?.numberDocument) &&
                touchedAccountholderGenerality?.numberDocument
              }
              id="number-document-accountholder"
              inputProps={{
                maxLength: '20',
              }}
              value={
                generality?.isFromAnpr &&
                generality?.numberDocument &&
                !touchedAccountholderGenerality?.numberDocument &&
                generality?.isForeignDocument === DocumentRadioCertificate.IdentityCard
                  ? `Carta d'identità n.${generality?.numberDocument}`
                  : generality?.numberDocument || ''
              }
              onChange={(event) => {
                formContext.setFieldTouched('accountholder.account.generality.numberDocument');
                onChangeFieldsAccountholder({
                  numberDocument: event?.target?.value,
                  isFromAnpr: false,
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Data rilascio *"
              aria-label="Data rilascio"
              value={generality?.dateReleaseDocument || null}
              onChange={(date) => {
                onChangeFieldAccountholder('dateReleaseDocument', date);
              }}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateReleaseDocumentAccountholder"
                  fullWidth
                  error={
                    Boolean(errorAccountholderGenerality?.dateReleaseDocument) &&
                    touchedAccountholderGenerality?.dateReleaseDocument
                  }
                  helperText={errorAccountholderGenerality?.dateReleaseDocument}
                />
              )}
            />
          </Grid>
          <Grid container item spacing={2}>
            {generality?.isForeignDocument === DocumentRadioCertificate.Other ? (
              <Grid item xs={12} md={7}>
                <PmiTextField
                  label="Luogo Rilascio"
                  aria-label="Luogo Rilascio"
                  id="release-location"
                  error={
                    Boolean(errorAccountholderGenerality?.consulateCode) &&
                    touchedAccountholderGenerality?.consulateCode
                  }
                  helperText={errorAccountholderGenerality?.consulateCode}
                  fullWidth
                  onChange={(event) => {
                    onChangeFieldAccountholder('consulateCode', event?.target.value);
                  }}
                  inputProps={{
                    maxLength: '120',
                  }}
                  value={generality?.consulateCode || ''}
                  disabled={false}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={7}>
                  <MunicipalityList
                    label="Comune *"
                    aria-label="Comune"
                    error={
                      Boolean(errorAccountholderGenerality?.municipalityDocument) &&
                      touchedAccountholderGenerality?.municipalityDocument
                    }
                    fromDate={generality?.dateReleaseDocument}
                    value={generality?.municipalityDocument || null}
                    disabled={false}
                    onChange={(value) => {
                      onChangeFieldAccountholder('municipalityDocument', value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    label="Provincia"
                    id="province"
                    inputProps={{
                      maxLength: '2',
                    }}
                    aria-label="Provincia"
                    fullWidth
                    onChange={(event) => {
                      onChangeFieldAccountholder('provinceDocument', event?.target.value);
                    }}
                    value={generality?.municipalityDocument?.province || ''}
                    disabled
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      ) : undefined}
    </Grid>
  );
};

AccordionApplicantFormComponent.displayName = 'Accordion applicant form component';

const AccordionApplicantForm = memo(AccordionApplicantFormComponent);

export default AccordionApplicantForm;
